import { apikeyConstants } from "../constants";
import { apikeyService } from "../services";

// SECURITY: src/helpers/logger.js console.logs every dispatched action and the
// full store. The raw apiKey returned by POST must therefore never be put in a
// dispatched action or in the store. generate() only dispatches non-secret
// fields and hands the raw key back to the calling component through the
// returned Promise; the component keeps it in local state only.
export const apikeyActions = {
    get,
    generate,
    revoke,
    clear
};

// Error by HTTP status only. Reads the body as text and tries JSON.parse, so an
// empty or non-JSON error body (e.g. a bare 401) never throws a parse error.
// A network failure (fetch rejects with a TypeError) becomes status 0.
function httpError(status, body) {
    const error = new Error('apikey request failed');
    error.status = status;
    error.body = body;
    return error;
}

function handleResponse(promise) {
    return promise.then(
        res => res.text().then(text => {
            let body;
            try {
                body = text ? JSON.parse(text) : undefined;
            } catch (e) {
                body = undefined;
            }
            if (!res.ok) {
                throw httpError(res.status, body);
            }
            return body || {};
        }),
        () => { throw httpError(0); }
    );
}

function toError(error) {
    return { status: error && typeof error.status === 'number' ? error.status : 0 };
}

function get() {
    return dispatch => {
        dispatch({ type: apikeyConstants.APIKEY_GET_REQUEST });

        return handleResponse(apikeyService.getApiKey())
            .then(data => {
                // GET never returns the raw key per the contract; strip it anyway
                // so it could not reach the logged action even if it did.
                const { apiKey, ...details } = data;
                dispatch({ type: apikeyConstants.APIKEY_GET_SUCCESS, payload: details });
            })
            .catch(error => dispatch({ type: apikeyConstants.APIKEY_GET_FAILURE, payload: toError(error) }));
    };
}

// Resolves with the raw key string (for the one-time reveal); rejects with
// { status } so the component can show the mapped message.
function generate() {
    return dispatch => {
        dispatch({ type: apikeyConstants.APIKEY_GENERATE_REQUEST });

        return handleResponse(apikeyService.generateApiKey())
            .then(data => {
                const rawKey = data.apiKey;
                dispatch({ type: apikeyConstants.APIKEY_GENERATE_SUCCESS });
                // Refresh the non-secret details (prefix, dates, remaining).
                dispatch(get());
                return rawKey;
            })
            .catch(error => {
                const err = toError(error);
                dispatch({ type: apikeyConstants.APIKEY_GENERATE_FAILURE, payload: err });
                throw err;
            });
    };
}

function revoke() {
    return dispatch => {
        dispatch({ type: apikeyConstants.APIKEY_REVOKE_REQUEST });

        return handleResponse(apikeyService.revokeApiKey())
            .then(() => {
                dispatch({ type: apikeyConstants.APIKEY_REVOKE_SUCCESS });
                dispatch(get());
            })
            .catch(error => {
                const err = toError(error);
                dispatch({ type: apikeyConstants.APIKEY_REVOKE_FAILURE, payload: err });
                throw err;
            });
    };
}

function clear() {
    return { type: apikeyConstants.APIKEY_CLEAR };
}
